import React from 'react';

import { cn } from '../../lib/utils';
import { formatLitres, formatNombre, formatPourcent } from '../../lib/formats';

/**
 * Jauge de cuve (programme Stations) : la cuve dessinée avec son niveau, le
 * seuil bas en pointillés, le volume en grand, la capacité et l'autonomie.
 * Le niveau passe à l'orange sous le seuil ; la couleur ne dit jamais seule
 * l'état : « sous le seuil » est écrit.
 *
 *  - `volume`, `capacity`, `threshold` : litres ;
 *  - `autonomyDays` : jours de vente couverts (facultatif) ;
 *  - `tone` : 'primary' (gasoil, par défaut) ou 'success' (super) ;
 *  - `variant` : 'ligne' (défaut, cuve à gauche et chiffres à droite) ou 'carte'
 *    (cuve haute au centre, niveau en pourcentage, marge avant le seuil et place libre).
 */
const borne = (valeur) => Math.max(0, Math.min(100, valeur));

const autonomieLue = (jours) => {
  if (jours === null || jours === undefined || jours === '') return null;
  const n = Number(jours);
  if (!Number.isFinite(n)) return null;
  return `${formatNombre(n, 1)} jour${n >= 2 ? 's' : ''}`;
};

export const TankGauge = ({
  name,
  productName,
  volume,
  capacity,
  threshold,
  autonomyDays,
  tone = 'primary',
  variant = 'ligne',
  className,
}) => {
  const litres = Number(volume) || 0;
  const capacite = Number(capacity) || 0;
  const seuil = Number(threshold) || 0;
  const niveau = capacite > 0 ? borne((litres / capacite) * 100) : 0;
  const ligneSeuil = capacite > 0 && seuil > 0 ? borne((seuil / capacite) * 100) : null;
  const bas = seuil > 0 && litres <= seuil;
  const remplissage = bas ? 'bg-flame' : tone === 'success' ? 'bg-success' : 'bg-primary';
  const autonomie = autonomieLue(autonomyDays);
  // Le produit n'est dit qu'une fois : « Cuve gasoil » suffit, « Cuve 1 » devient « Cuve 1 · Gasoil ».
  const dejaDit = name && productName && String(name).toLowerCase().includes(String(productName).toLowerCase());
  const nom = [name, dejaDit ? null : productName].filter(Boolean).join(' · ');

  if (variant === 'carte') {
    const avantSeuil = seuil > 0 ? Math.max(0, litres - seuil) : null;
    const placeLibre = capacite > 0 ? Math.max(0, capacite - litres) : null;
    return (
      <div className={cn('flex w-48 flex-col items-center gap-2 text-center', className)}>
        <div
          className="relative h-48 w-28 overflow-hidden rounded-b-xl rounded-t-[28px] border-2 border-foreground/80 bg-muted shadow-inner"
          role="meter"
          aria-valuemin={0}
          aria-valuemax={capacite || undefined}
          aria-valuenow={litres}
          aria-label={`${nom || 'Cuve'} : ${formatLitres(litres)} sur ${formatLitres(capacite)}`}
        >
          <div
            className={cn('absolute inset-x-0 bottom-0 transition-[height] duration-700 ease-out motion-reduce:transition-none', remplissage)}
            style={{ height: `${niveau}%` }}
          />
          {[25, 50, 75].map((repere) => (
            <div
              key={repere}
              className="absolute right-0 w-3 border-t border-foreground/40"
              style={{ bottom: `${repere}%` }}
              aria-hidden="true"
            />
          ))}
          {ligneSeuil !== null && (
            <div
              className="absolute -left-0.5 -right-0.5 border-t-2 border-dashed border-destructive"
              style={{ bottom: `${ligneSeuil}%` }}
              aria-hidden="true"
            />
          )}
        </div>
        <p className="m-0 break-words text-sm font-semibold text-foreground" title={nom || undefined}>{nom || 'Cuve'}</p>
        <p className="m-0 font-display text-3xl font-bold leading-none tabular-nums">{formatLitres(litres)}</p>
        <p className={cn('m-0 rounded-full px-2.5 py-0.5 text-xs font-semibold tabular-nums', bas ? 'bg-destructive/10 text-destructive' : 'bg-muted text-muted-foreground')}>
          {bas ? `Sous le seuil · ${formatPourcent(niveau, 0)}` : `${formatPourcent(niveau, 0)} rempli`}
        </p>
        <dl className="m-0 grid w-full gap-1 text-xs">
          {avantSeuil !== null && (
            <div className="flex items-baseline justify-between gap-2">
              <dt className="text-muted-foreground">Avant le seuil</dt>
              <dd className="m-0 font-medium tabular-nums">{formatLitres(avantSeuil)}</dd>
            </div>
          )}
          {placeLibre !== null && (
            <div className="flex items-baseline justify-between gap-2">
              <dt className="text-muted-foreground">Place libre</dt>
              <dd className="m-0 font-medium tabular-nums">{formatLitres(placeLibre)}</dd>
            </div>
          )}
          <div className="flex items-baseline justify-between gap-2">
            <dt className="text-muted-foreground">Capacité</dt>
            <dd className="m-0 font-medium tabular-nums">{formatLitres(capacite)}</dd>
          </div>
          {autonomie && (
            <div className="flex items-baseline justify-between gap-2">
              <dt className="text-muted-foreground">Autonomie</dt>
              <dd className="m-0 font-medium tabular-nums">{autonomie}</dd>
            </div>
          )}
        </dl>
      </div>
    );
  }

  return (
    <div className={cn('grid grid-cols-[3.4rem_minmax(0,1fr)] items-end gap-3', className)}>
      <div
        className="relative h-36 overflow-hidden rounded-b-md rounded-t-[10px] border-2 border-foreground/80 bg-muted"
        role="meter"
        aria-valuemin={0}
        aria-valuemax={capacite || undefined}
        aria-valuenow={litres}
        aria-label={`${nom || 'Cuve'} : ${formatLitres(litres)} sur ${formatLitres(capacite)}`}
      >
        <div
          className={cn('absolute inset-x-0 bottom-0 transition-[height] duration-700 ease-out motion-reduce:transition-none', remplissage)}
          style={{ height: `${niveau}%` }}
        />
        {ligneSeuil !== null && (
          <div
            className="absolute -left-0.5 -right-0.5 border-t-2 border-dashed border-destructive"
            style={{ bottom: `${ligneSeuil}%` }}
            aria-hidden="true"
          />
        )}
      </div>
      <dl className="m-0 grid min-w-0 gap-0.5">
        <dt className="break-words text-xs text-muted-foreground" title={nom || undefined}>{nom || 'Cuve'}</dt>
        <dd className="m-0 mb-1 font-display text-2xl font-bold leading-none tabular-nums">{formatLitres(litres)}</dd>
        <dt className="text-xs text-muted-foreground">sur {formatLitres(capacite)}</dt>
        {bas && <dd className="m-0 text-sm font-medium text-destructive">Sous le seuil</dd>}
        {autonomie && <dd className={cn('m-0 text-sm tabular-nums', bas && 'text-destructive')}>{autonomie}</dd>}
      </dl>
    </div>
  );
};

export default TankGauge;
